import { AmortizationType } from "../../amortization";
import { HousingType, PrivateLoanType } from "../../forecast";
import { ChildHouseholdRate } from "../../household";
import { calculateYearlyInterestExpense, getBestInterestDataByLTV } from "../../interest";
import { MarketSE } from "../../localization";
import { deepClone, sum } from "../../utils";
import { PRIVATE_LOAN_INTEREST_DEDUCTION_REDUCTION_RATE } from "../../localization/se";
const MONTHS_IN_YEAR = 12;
/**
 * Sums all loans amounts and amortizations into one loan and sets argument interest to loan
 *
 * @param interest One interest rate for all loans in mortgage
 * @param mortgage Mortgage data
 * @returns Mortgage as one loan
 */
function simplifyMortgage(interest, mortgage) {
    if (!mortgage) {
        return {
            amount: 0,
            interestRate: interest,
            monthlyAmortization: 0,
        };
    }
    return {
        amount: sum(mortgage, (loan) => loan.amount),
        interestRate: interest,
        monthlyAmortization: sum(mortgage, (loan) => loan.monthlyAmortization),
    };
}
/**
 * Calculates interest deduction threshold with additional mortgage amount deducted.
 *
 * @param deductionTreshold Interest deduction threshold
 * @param additionalMortgage Additional mortgage data as one loan
 * @param privateLoans Private loans
 * @param adults Number of adults in household
 * @returns Interest deduction threshold with additional mortgage amount deducted.
 */
function calculateInterestDeductionThresholdForMainMortgage(interestDeductionThresholdPerAdult, additionalMortgage, privateLoans, adults) {
    const additionalMortgageInterestExpenseWithoutDeduction = calculateYearlyInterestExpense(additionalMortgage.amount, additionalMortgage.monthlyAmortization, additionalMortgage.interestRate);
    const privateLoansInterestExpenseWithoutDeduction = sum(privateLoans, (loan) => loan.type === PrivateLoanType.INSTALMENT_CREDIT || loan.type === PrivateLoanType.UNSECURED_LOAN
        ? calculateYearlyInterestExpense(loan.amount, loan.amortization, loan.interestRate) * PRIVATE_LOAN_INTEREST_DEDUCTION_REDUCTION_RATE
        : 0);
    const nrOfAdultsResponsibleForMortgages = Math.min(adults, 2);
    const fullInterestDeductionThreshold = interestDeductionThresholdPerAdult * nrOfAdultsResponsibleForMortgages;
    const interestDeductionThresholdForMainMortgage = Math.max(fullInterestDeductionThreshold - additionalMortgageInterestExpenseWithoutDeduction - privateLoansInterestExpenseWithoutDeduction, 0);
    return interestDeductionThresholdForMainMortgage;
}
/**
 * Creates array of swedish amortization requirement loan-to-value ratios
 *
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Array with swedish amortization requirement loan-to-value ratios
 */
function getDownPaymentRatiosByLTVRequirement(localizationParameters) {
    const { highLoanToValueThreshold, lowLoanToValueThreshold } = localizationParameters;
    const lowDownPaymentRatio = Math.round((1 - highLoanToValueThreshold) * 100) / 100;
    const highDownPaymentRatio = Math.round((1 - lowLoanToValueThreshold) * 100) / 100;
    return [lowDownPaymentRatio, highDownPaymentRatio];
}
/**
 * Calculates down payment ratio for swedish amortization requirement debt-to-income
 *
 * @param isStaticDownPayment Down payment amount is static, to the contrary of static housing value
 * @param housingValue Housing value
 * @param downPayment Down payment
 * @param salaryGrossIncome Household salary gross income
 * @param additionalMortgageAmount Additional mortgage data as one loan
 * @param debtToIncomeFactor Amortization requirement debt-to-income factor
 * @returns Down payment ratio at amortization requirement debt-to-income
 */
function getDownpaymentRatioByIncomeRequirement(isStaticDownPayment, housingValue, downPayment, salaryGrossIncome, additionalMortgageAmount, debtToIncomeFactor) {
    let calculatedHousingValue = housingValue;
    let calculatedDownPayment = downPayment;
    const maxMortgageValueByIncome = salaryGrossIncome * MONTHS_IN_YEAR * debtToIncomeFactor - additionalMortgageAmount;
    if (maxMortgageValueByIncome <= 0) {
        return 1;
    }
    if (isStaticDownPayment) {
        calculatedHousingValue = maxMortgageValueByIncome + downPayment;
    }
    else {
        calculatedDownPayment = housingValue - maxMortgageValueByIncome;
    }
    return calculatedDownPayment / calculatedHousingValue;
}
/**
 * Calculates down payment ratio when mortgage is at interest deduction threshold
 *
 * @param isStaticDownPayment Down payment amount is static, to the contrary of static housing value
 * @param housingValue Housing value
 * @param downPayment Down payment
 * @param calculationInterestRate Bank calculation interest rate
 * @param deductionTreshold Interest deduction threshold
 * @returns Down payment ratio when mortgage amount is at interest deduction threshold
 */
function getDownPaymentRatioAtInterestDeductionThreshold(isStaticDownPayment, housingValue, downPayment, calculationInterestRate, deductionTreshold) {
    let downPaymentRatio;
    const mortgageAmount = deductionTreshold / calculationInterestRate;
    if (isStaticDownPayment) {
        const calculatedHousingValue = mortgageAmount + downPayment;
        downPaymentRatio = downPayment / calculatedHousingValue;
    }
    else {
        const calculatedDownPayment = housingValue - mortgageAmount;
        downPaymentRatio = calculatedDownPayment / housingValue;
    }
    return downPaymentRatio;
}
/**
 * Calculates all down payment ratios at changes in main mortgage required amortization rate or interest deduction rate
 *
 * @param minDownPaymentRatio Min down payment ratio
 * @param isStaticDownPayment Down payment amount is static, to the contrary of static housing value
 * @param housingValue Housing value
 * @param downPayment Down payment amount
 * @param calculationInterestRate Bank calculation interest rate
 * @param salaryGrossIncome Household salary gross income
 * @param additionalMortgageAmount Additional mortgage data as one loan
 * @param currentInterestDeductionThreshold Current interest deduction threshold with additional mortgage interest deducted
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @param customLoanToValueAmortizationRate Optional bypass amortization rate for loan-to-value ratio requirement
 * @returns All down payment ratios at changes in main mortgage required amortization rate or interest deduction rate
 */
function calculateDownPaymentRatiosAtThresholds(minDownPaymentRatio, isStaticDownPayment, housingValue, downPayment, calculationInterestRate, salaryGrossIncome, additionalMortgageAmount, currentInterestDeductionThreshold, localizationParameters, customLoanToValueAmortizationRate) {
    const downPaymentRatios = [minDownPaymentRatio, 1];
    if (!customLoanToValueAmortizationRate) {
        downPaymentRatios.push(...getDownPaymentRatiosByLTVRequirement(localizationParameters));
    }
    const downPaymentRatioByIncome = getDownpaymentRatioByIncomeRequirement(isStaticDownPayment, housingValue, downPayment, salaryGrossIncome, additionalMortgageAmount, localizationParameters.amortizationDebtToIncomeFactor);
    if (downPaymentRatioByIncome > minDownPaymentRatio && downPaymentRatioByIncome < 1) {
        downPaymentRatios.push(downPaymentRatioByIncome);
    }
    const downPaymentRatioAtInterestDeductionThreshold = getDownPaymentRatioAtInterestDeductionThreshold(isStaticDownPayment, housingValue, downPayment, calculationInterestRate, currentInterestDeductionThreshold);
    if (downPaymentRatioAtInterestDeductionThreshold > minDownPaymentRatio && downPaymentRatioAtInterestDeductionThreshold < 1) {
        downPaymentRatios.push(downPaymentRatioAtInterestDeductionThreshold);
    }
    return downPaymentRatios.sort((a, b) => a - b);
}
/**
 * Calculates closest down payment interval for max mortgage amount
 *
 * @param isStaticDownPayment Down payment amount is static, to the contrary of static housing value
 * @param downPaymentRatioThresholds All down payment ratio thresholds that impacts max mortgage calculations
 * @param downPayment Down payment amount
 * @param input Forecast input for swedish market
 * @param forecastTool Instance of forecast class
 * @returns Down payment interval for max mortgage amount
 */
function determineDownpaymentRatioInterval(isStaticDownPayment, downPaymentRatioThresholds, downPayment, input, forecastTool) {
    const inputClone = deepClone(input);
    const intervalData = {
        lowestDownPaymentRatio: downPaymentRatioThresholds[0],
        highestDownPaymentRatio: 0,
        mortgageExpenseDelta: 0,
    };
    for (let i = 0; i < downPaymentRatioThresholds.length; i++) {
        const threshold = downPaymentRatioThresholds[i];
        if (isStaticDownPayment) {
            const calculatedHousingValue = Math.floor(downPayment / threshold);
            inputClone.housingValue = calculatedHousingValue;
            inputClone.mortgage[0].amount = calculatedHousingValue - downPayment;
        }
        else {
            const calculatedDownPayment = Math.ceil(input.housingValue * threshold);
            inputClone.mortgage[0].amount = input.housingValue - calculatedDownPayment;
        }
        const { bankDiscreationaryIncome } = forecastTool.forecastAll(inputClone, MONTHS_IN_YEAR);
        if (bankDiscreationaryIncome <= 0) {
            intervalData.lowestDownPaymentRatio = threshold;
            intervalData.mortgageExpenseDelta = Math.abs(bankDiscreationaryIncome) * MONTHS_IN_YEAR + 1;
            intervalData.highestDownPaymentRatio = threshold;
        }
        else {
            intervalData.highestDownPaymentRatio = threshold;
            break;
        }
    }
    return intervalData;
}
/**
 * @param args arguments for max mortgage amount calculation
 * @returns max mortgage amount
 */
function calculateMaxMortgageAmount(args) {
    const { intervalData, isStaticDownPayment, housingValue, downPayment, additionalMortgageAmount, householdSalaryIncome, calculationInterestRate, currentInterestDeductionThreshold, adults, localizationParameters, customLoanToValueAmortizationRate, } = args;
    const { lowestDownPaymentRatio, highestDownPaymentRatio, mortgageExpenseDelta } = intervalData;
    const { baseInterestDeductionRate, extraInterestDeductionRate } = localizationParameters;
    const highestHousingValue = isStaticDownPayment ? Math.floor(downPayment / lowestDownPaymentRatio) : housingValue;
    const highestMortgageAmount = isStaticDownPayment
        ? highestHousingValue - downPayment
        : housingValue - Math.ceil(housingValue * lowestDownPaymentRatio);
    const totalMortgageAmount = highestMortgageAmount + additionalMortgageAmount;
    const amortizationRateByLTV = customLoanToValueAmortizationRate !== null && customLoanToValueAmortizationRate !== void 0 ? customLoanToValueAmortizationRate : MarketSE.getAmortizationRateByLoanToValueRequirement(highestHousingValue, highestMortgageAmount, localizationParameters);
    const amortizationRate = amortizationRateByLTV + MarketSE.getAmortizationRateByDebtToIncome(totalMortgageAmount, householdSalaryIncome, localizationParameters);
    const amortization = highestMortgageAmount * amortizationRate;
    const interestExpense = highestMortgageAmount * calculationInterestRate;
    const updatedLocalizationParameters = Object.assign(Object.assign({}, localizationParameters), { interestDeductionThreshold: currentInterestDeductionThreshold });
    // The interest deduction threshold is set so that the additional mortgages interest and private loans interest are not relevant here.
    const interestExpenseDeduction = MarketSE.calculateMultipleLoansInterestDeduction({ yearMainMortgageInterest: interestExpense, yearAdditionalMortgageInterest: 0, yearPrivateLoanInterest: 0 }, adults, updatedLocalizationParameters);
    const interestExpensesAfterDeduction = interestExpense - interestExpenseDeduction.mainMortgageInterestDeduction;
    const useExtraDeductionRate = interestExpense > currentInterestDeductionThreshold;
    const baseDeductionFactor = Math.round((1 - baseInterestDeductionRate) * 100) / 100;
    const extraDeductionFactor = Math.round((1 - extraInterestDeductionRate) * 100) / 100;
    let mortgageExpenseForMaxMortgage = amortization + interestExpensesAfterDeduction - mortgageExpenseDelta;
    let mortgageExpensesRate;
    if (useExtraDeductionRate) {
        const baseDeductionConversion = currentInterestDeductionThreshold * (baseInterestDeductionRate - extraInterestDeductionRate);
        mortgageExpenseForMaxMortgage += baseDeductionConversion;
        const interestExpenseRate = extraDeductionFactor * calculationInterestRate;
        mortgageExpensesRate = amortizationRate + interestExpenseRate;
    }
    else {
        const interestExpenseRate = baseDeductionFactor * calculationInterestRate;
        mortgageExpensesRate = amortizationRate + interestExpenseRate;
    }
    const calculatedMortgageAmount = mortgageExpenseForMaxMortgage / mortgageExpensesRate;
    const mortgageByHighestDownPaymentRatio = isStaticDownPayment
        ? downPayment / highestDownPaymentRatio - downPayment
        : housingValue * (1 - highestDownPaymentRatio);
    const maxMortgageAmount = calculatedMortgageAmount > mortgageByHighestDownPaymentRatio ? calculatedMortgageAmount : mortgageByHighestDownPaymentRatio;
    return maxMortgageAmount;
}
/**
 * Calculates max debt amount by bank max debt-to-income factor
 *
 * @param householdSalaryIncome Household salary income
 * @param debtFactor Max debt-to-income factor
 * @returns Max debt by bank max debt-to-income factor
 */
function calculateMaxDebtByBankMaxDebtToIncomeFactor(householdSalaryIncome, debtFactor = 5) {
    const yearlyHouseholdSalaryIncome = householdSalaryIncome * MONTHS_IN_YEAR;
    const maxDebtAmount = yearlyHouseholdSalaryIncome * debtFactor;
    return maxDebtAmount;
}
/**
 * Calculates max mortgage amount and max housing price
 *
 * @param input Forecast input data for swedish market with market specific amortization regulations
 * @param isStaticDownPayment Down payment amount is static, to the contrary of static housing value
 * @param minDownPaymentRatio Min down payment ratio
 * @param localizationParameters Forecast localization parameters
 * @param roundingPrecision Rounding precision ≠ 0, e.g. 1000 for thousands
 * @param maxDebtByYearIncomeFactor Optional max mortgage amount by year income factor
 * @returns Down payment, max mortgage amount and max housing price
 */
function estimateBorrowingSpace(input, isStaticDownPayment, minDownPaymentRatio, localizationParameters, roundingPrecision = 1000, maxDebtByYearIncomeFactor) {
    var _a, _b;
    if (roundingPrecision === 0 || ((_a = input.mortgage) === null || _a === void 0 ? void 0 : _a.length) <= 0) {
        return {
            downPayment: -1,
            maxMortgage: -1,
            housingPrice: -1,
        };
    }
    const inputClone = deepClone(input);
    const forecastTool = new MarketSE.Forecast(localizationParameters);
    const mortgageSummary = simplifyMortgage(inputClone.calculationInterestRate, inputClone.mortgage);
    const additionalMortgageSummary = simplifyMortgage(inputClone.calculationInterestRate, (_b = inputClone.additionalHousing) === null || _b === void 0 ? void 0 : _b.mortgage);
    inputClone.mortgage = [mortgageSummary];
    const downPayment = inputClone.housingValue - mortgageSummary.amount;
    const salaryGrossIncome = sum(inputClone.adults, (adult) => Math.round(adult.income * adult.employmentRate));
    const interestDeductionThresholdForMainMortgage = calculateInterestDeductionThresholdForMainMortgage(localizationParameters.mortgage.interestDeductionThreshold, additionalMortgageSummary, inputClone.privateLoans, inputClone.adults.length);
    const downPaymentRatioThresholds = calculateDownPaymentRatiosAtThresholds(minDownPaymentRatio, isStaticDownPayment, inputClone.housingValue, downPayment, inputClone.calculationInterestRate, salaryGrossIncome, additionalMortgageSummary.amount, interestDeductionThresholdForMainMortgage, localizationParameters.mortgage, input.customLoanToValueAmortizationRate);
    const downPaymentRatioInterval = determineDownpaymentRatioInterval(isStaticDownPayment, downPaymentRatioThresholds, downPayment, inputClone, forecastTool);
    if (downPaymentRatioInterval.highestDownPaymentRatio === minDownPaymentRatio) {
        return {
            downPayment: input.housingValue * minDownPaymentRatio,
            maxMortgage: input.housingValue - input.housingValue * minDownPaymentRatio,
            housingPrice: input.housingValue,
        };
    }
    if (downPaymentRatioInterval.highestDownPaymentRatio === downPaymentRatioInterval.lowestDownPaymentRatio) {
        return {
            downPayment: input.housingValue,
            maxMortgage: 0,
            housingPrice: input.housingValue,
        };
    }
    const maxMortgageAmountByForecast = calculateMaxMortgageAmount({
        intervalData: downPaymentRatioInterval,
        isStaticDownPayment,
        housingValue: input.housingValue,
        downPayment,
        additionalMortgageAmount: additionalMortgageSummary.amount,
        householdSalaryIncome: salaryGrossIncome,
        calculationInterestRate: inputClone.calculationInterestRate,
        currentInterestDeductionThreshold: interestDeductionThresholdForMainMortgage,
        adults: input.adults.length,
        localizationParameters: localizationParameters.mortgage,
        customLoanToValueAmortizationRate: input.customLoanToValueAmortizationRate,
    });
    const privateLoanSum = sum(inputClone.privateLoans, (loan) => (loan.type === PrivateLoanType.STUDENT_LOAN ? 0 : loan.amount));
    const maxDebtByForecast = maxMortgageAmountByForecast + additionalMortgageSummary.amount + privateLoanSum;
    const useMaxDebtAmountByIncome = typeof maxDebtByYearIncomeFactor === "number" && salaryGrossIncome * maxDebtByYearIncomeFactor * MONTHS_IN_YEAR < maxDebtByForecast;
    const maxDebt = useMaxDebtAmountByIncome ? salaryGrossIncome * maxDebtByYearIncomeFactor * MONTHS_IN_YEAR : maxDebtByForecast;
    const estimatedBorrowingSpace = Math.max(maxDebt - additionalMortgageSummary.amount - privateLoanSum, 0);
    const estimatedBorrowingSpaceRounded = Math.floor(estimatedBorrowingSpace / roundingPrecision) * roundingPrecision;
    const housingPrice = isStaticDownPayment ? estimatedBorrowingSpaceRounded + downPayment : input.housingValue;
    const estimatedDownPayment = isStaticDownPayment ? downPayment : input.housingValue - estimatedBorrowingSpaceRounded;
    return {
        downPayment: estimatedDownPayment,
        maxMortgage: estimatedBorrowingSpaceRounded,
        housingPrice,
    };
}
const { calculatePropertyTax, getMunicipalities, Forecast } = MarketSE;
export { AmortizationType, ChildHouseholdRate, Forecast, HousingType, PrivateLoanType, calculateMaxDebtByBankMaxDebtToIncomeFactor, calculatePropertyTax, deepClone, estimateBorrowingSpace, getBestInterestDataByLTV, getMunicipalities, };
